import React, { useState, useCallback } from 'react';
import MasterSpreadsheet from './MasterSpreadsheet';
import SectionTitle from './text_components/SectionTitle';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import RequirementsChecklistSelectionForm from './RequirementsChecklistSelectionForm';
import AIRewriter from './AIRewriter';
import { useParams } from 'react-router-dom';
import AutomaticDataEntryForFundPerformance from './automatic_data_entry/AutomaticDataEntryForFundPerformance';
import { useCompany } from '../contexts/CompanyContext';

// Import Add New Row components
import AutomaticDataEntryForPlanTouchpoints from './automatic_data_entry/AutomaticDataEntryForPlanTouchpoints';
import AutomaticDataEntryRequirementsChecklist from './automatic_data_entry/AutomaticDataEntryForRequirementsChecklist';
import AutomaticDataEntryForClientList from './automatic_data_entry/AutomaticDataEntryForClientList';
import AutomaticDataEntryForPlanDesignAndElections from './automatic_data_entry/AutomaticDataEntryForPlanDesignAndElections';
import AutomaticDataEntryForPlanPerformance from './automatic_data_entry/AutomaticDataEntryForPlanPerformance';
import AutomaticDataEntryForAdvisorServiceSchedule from './automatic_data_entry/AutomaticDataEntryForAdvisorServiceSchedule';
import AutomaticDataEntryForFeeSchedule from './automatic_data_entry/AutomaticDataEntryForFeeSchedule';
import AutomaticDataEntryForPlanParticipants from './automatic_data_entry/AutomaticDataEntryForPlanParticipants';
import AutomaticDataEntryForClientProspecting from './automatic_data_entry/AutomaticDataEntryForClientProspecting';


const DataLoaderWithQuery = ({ username, year, isSidebarOpen }) => {
  const { spreadsheetName } = useParams();
  const { isCustomerView } = useCompany();
  
  const getSpreadsheetNameFromUrl = (urlName) => {
    const nameMap = {
      'client-list': 'Client List',
      'plan-touchpoints': 'Plan Touchpoints',
      'requirements-checklist': 'Requirements Checklist',
      'plan-design-and-elections': 'Plan Design and Elections',
      'plan-performance': 'Plan Performance',
      'fund-performance': 'Fund Performance',
      'advisor-service-schedule': 'Advisor Service Schedule',
      'fee-schedule': 'Fee Schedule',
      'participants-table': 'Participants Table',
      'prospecting': 'Prospecting'
    };
    return nameMap[urlName] || urlName;
  };
  
  const currentSpreadsheet = getSpreadsheetNameFromUrl(spreadsheetName);
  
  const [showAddRow, setShowAddRow] = useState(false);
  const [showAutomaticDataEntry, setShowAutomaticDataEntry] = useState(false);
  const [showRequirementsForm, setShowRequirementsForm] = useState(false);
  const [showAIRewriter, setShowAIRewriter] = useState(false);

  // Define initial column widths for all spreadsheet types
  const initialColumnWidths = {
    "Company Name": 250,
    "TPA": 250,
    "Plan Sponsor Contact Info": 250,
    "Contract Number": 250,
    "Recordkeeper": 250,
    "Recordkeeper Contact Info": 350,
    "TPA Contact Info": 350,
    "Company Administrators": 350,
    "Advisor / Plan Manager": 350,
    "Notes": 500,
    "Touchpoints": 600,
    "AI Rewritten Touchpoints": 600,
    "Stakeholders": 200,
    "Include In Annual Review Report": 300,
    "Include In Next Year's Plan Data": 300,
    "Time Spent": 250,
    "Date": 250,
    "Requirement": 600,
    "AI Rewritten Requirement": 600,
    "Complete": 250,
    "Due Date": 250,
    "Priority": 250,
    "Plan Type": 600,
    "Eligibility Requirements": 600,
    "Automatic Enrollment": 600,
    "Entry Date": 600,
    "Eligible Contribution Types": 600,
    "Contribution Compensation Definition": 600,
    "Roth Contributions Allowed": 600,
    "Roth Contributions Rules or Conditions": 600,
    "Catch-Up Contributions Allowed": 600,
    "Employer Match Formula": 600,
    "True-Up Provision": 600,
    "Roth Contribution Rules or Conditions": 600,
    "Loans (Availability and Details)": 600,
    "Hardship Distributions": 600,
    "In Service Distributions": 600,
    "Employer Matching Vesting Schedule": 600,
    "Non-Elective Vesting Schedule": 600,
    "Vesting": 600,
    "QDIA": 600,
    "Advisor's Note (Optional)": 600,
    "Advisor Yearly Compensation": 250,
    'Plan Size': 250,
    "Participation Rate": 250,
    "Number of Participants": 250,
    "Deferral Rate": 250,
    "Average Account Balance": 250,
    "Loan Withdrawal Usage": 250,
    "Employees Registered Online": 250,
    "Employees Opting For Paper Statements": 400,
    "Interaction Rate": 250,
    "Number of Plans Available": 250,
    "Employees Build Their Own Portfolio": 400,
    "Use Target or Investment Models": 400,
    "Investments (Leave Blank)": 400,
    "Comprehensive Plan Review": 400,
    "Review Investment Policy Statement": 400,
    "Review Investment Menu, Performance, and Providers": 500,
    "Select Alternatives to Address Over and Under Concentration": 700,
    "Fiduciary Responsibilities (Leave Blank)": 400,
    "Serve as a Co-Fiduciary Under ERISA": 400,
    "Assess fidelity bonding": 400,
    "Compliance Check & Fee Disclosure": 400,
    "Employee Education (Leave Blank)": 400,
    "Assess Current Employee Education Program": 400,
    "Plan and Implement Effective Employee Education Program": 600,
    "Targeted Education Meetings, Including Re-Enrollment": 500,
    "Limited One-On-One Support Via Phone": 400,
    "Advisor Fee": 250,
    "Recordkeeper Fee": 250,
    "TPA Fee": 250,
    "Funds Fee": 250,
    "Date Confirmed": 250,
    "Fund Name": 450,
    "Type": 250,
    "Peer Group": 250,
    "Fi360 Fiduciary Score": 250,
    "Watch List Status": 250,
    "Reason for Watch List Status": 750,
    
    // Add more column widths as needed
  };



  const handleAddRow = useCallback(() => {
    window.location.reload();
  }, []);

  const handleToggleAutomaticEntry = () => {
    setShowAutomaticDataEntry(!showAutomaticDataEntry);
  };

  const handleToggleRequirementsForm = () => {
    setShowRequirementsForm(!showRequirementsForm);
  };

  const handleToggleAIRewriter = () => {
    setShowAIRewriter(!showAIRewriter);
  };

  const handleCloseAutomaticEntry = () => {
    setShowAutomaticDataEntry(false);
  };

  const renderAutomaticDataEntry = () => {
    const componentMap = {
      'Client List': AutomaticDataEntryForClientList,
      'Plan Touchpoints': AutomaticDataEntryForPlanTouchpoints,
      'Plan Design and Elections': AutomaticDataEntryForPlanDesignAndElections,
      'Plan Performance': AutomaticDataEntryForPlanPerformance,
      'Fund Performance': AutomaticDataEntryForFundPerformance,
      'Advisor Service Schedule': AutomaticDataEntryForAdvisorServiceSchedule,
      'Fee Schedule': AutomaticDataEntryForFeeSchedule,
      'Plan Participants': AutomaticDataEntryForPlanParticipants,
      'Client Prospecting': AutomaticDataEntryForClientProspecting,
      'Requirements Checklist': AutomaticDataEntryRequirementsChecklist,
    };

    const Component = componentMap[currentSpreadsheet];
    
    if (!Component) return null;

    return (
      <AnimatePresence>
        {showAutomaticDataEntry && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-1/2 bg-white shadow-xl z-50 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Add New {currentSpreadsheet} Entry</h2>
                <button
                  onClick={handleCloseAutomaticEntry}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              <Component 
                onAddRow={handleAddRow}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <MasterSpreadsheet
          key={year}
          onAddRow={handleAddRow}
          year={year}
          currentSheetType={currentSpreadsheet}
          onToggleAutomaticEntry={!isCustomerView ? handleToggleAutomaticEntry : null}
          onToggleRequirementsForm={!isCustomerView ? handleToggleRequirementsForm : null}
          initialColumnWidths={initialColumnWidths}
        />
      </div>

      {renderAutomaticDataEntry()}

      {showRequirementsForm && currentSpreadsheet === 'Requirements Checklist' && (
        <RequirementsChecklistSelectionForm 
          isOpen={showRequirementsForm}
          onClose={() => setShowRequirementsForm(false)}
          onSuccess={() => {
            setShowRequirementsForm(false);
            window.location.reload();
          }}
        />
      )}

      {showAIRewriter && currentSpreadsheet === 'Plan Touchpoints' && (
        <AIRewriter
          isOpen={showAIRewriter}
          onClose={() => setShowAIRewriter(false)}
          onRewrite={handleAddRow}
        />
      )}
    </div>
  );
};

DataLoaderWithQuery.propTypes = {
  username: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired
};

export default DataLoaderWithQuery;